import type { HeartsMove, HeartsView } from "@bing/engine-hearts";
import { useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MatchEndCard } from "../components/MatchEndCard";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import { faDigits } from "../i18n/digits";
import { HeartsTable } from "../screens/HeartsTable";
import { heartsSeatFallback } from "./hearts-seat-label";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatAvatar,
  type SeatIdentity,
  type SeatPlayer,
} from "./seat-identity";
import { getBotDifficulty, getCardDeck, setBotDifficulty, setCardDeck } from "../settings/prefs";
import type { BotDifficulty, CardDeckId } from "@bing/shared";
import { SoloStartScreen } from "../components/SoloStartScreen";
import { takeResumeIntent } from "./fresh-solo";
import { useSession } from "../store/context";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { useEndReveal } from "./use-end-reveal";
import type { GameResult } from "./connection";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";
import { CardDeckContext } from "./card-decks";
import { useDeckGate } from "./use-card-faces";
import { handKeyOf, useHandDeck } from "./hand-deck";

const AVATAR_IDS = ["lion", "wolf", "tiger", "eagle"] as const;

/** The design's win overlay: dimmed table, trophy, sorted final scores, and
 * play-again / exit. Hearts is lowest-score-wins, so scores sort ascending.
 * Exported so the online game reuses the exact same end-of-match overlay.
 *
 * ⚠️ IT MUST BE TOLD WHO THE SEATS ARE. This card is shared by solo and online, and for online it
 * used to receive no roster at all — so it labelled every seat but yours `«ربات <absolute seat>»`
 * (Latin digits; seat 0 read «ربات 0») even at a table of four real people, while the score box
 * a few pixels above showed their real names. `players`/`self` are the same pair the table
 * takes, resolved through the same {@link resolveSeatName} / {@link resolveSeatAvatar}, so the
 * result card and the felt can never disagree about a chair again.
 * [Reported from prod 2026-08-29; reproduced live on staging room `DnBuBqLCH`.]
 */
export function WinOverlay({
  result,
  totalScores,
  you,
  self,
  players,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  result: GameResult;
  totalScores: readonly number[];
  you: number;
  /** The signed-in user's identity — their own row wears their real photo + name. */
  self?: SeatIdentity | null;
  /** Known real occupants per seat (online). Solo → undefined, and the seats read «بات n». */
  players?: readonly SeatPlayer[];
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const seats = totalScores.length;
  // A TIE IS NOT A WIN. `winners` lists every seat on the minimum, so `includes(you)` alone told
  // BOTH players of a 42–42 finish that they had won (prod room `saeYC1kwg`). Only the engine can
  // tell a joint-best seat from an outright winner, and it now does — `tied`.
  const inWinners = result.winners.includes(you);
  const drawn = result.tied === true && inWinners;
  const won = inWinners && !drawn;
  const ranked = totalScores
    .map((score, seat) => ({ seat, score }))
    .sort((a, b) => a.score - b.score);
  const ranks = ["①", "②", "③", "④"];
  // Equal scores share a place — joint first is ① and ①, never ① and ②.
  const rankOf = (score: number): string =>
    ranks[totalScores.filter((s) => s < score).length] ?? "④";
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: fa.hearts.you,
      fallback: (st) => heartsSeatFallback(st, you, seats, players),
    });
  const avatarOf = (seat: number): SeatAvatar =>
    resolveSeatAvatar(
      seat,
      you,
      self,
      players,
      (st) => AVATAR_IDS[st % AVATAR_IDS.length] ?? "fox",
    );

  // card-98 item 12: the shared template — the winning feel, each PLAYER a side (no teams in بی‌دل):
  // its own rank (equal totals share a place), face, name and total, lowest first
  return (
    <MatchEndCard
      label={fa.hearts.gameOver}
      outcome={won ? "won" : drawn ? "drawn" : "lost"}
      glyph="🎴"
      title={won ? fa.hearts.youWon : drawn ? fa.hearts.drawn : fa.hearts.youLost}
      reason={drawn ? fa.hearts.drawnHint(faDigits(totalScores[you] ?? 0)) : fa.hearts.scores}
      sides={ranked.map((r) => ({
        key: `seat-${r.seat}`,
        mine: r.seat === you,
        mark: <span style={{ color: "var(--accent)", fontWeight: 800 }}>{rankOf(r.score)}</span>,
        members: [{ seat: r.seat, name: nameOf(r.seat), avatar: avatarOf(r.seat) }],
        result: faDigits(r.score),
        attrs: { "data-seat": r.seat },
      }))}
      promo
      playAgain={fa.hearts.playAgain}
      leave={fa.hearts.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/** Owns the Hearts connection + store, and renders connect/error/table/game-over.
 * Since the 2026-07-10 rebuild solo بی‌دل rides the GENERIC paced room path
 * (`makeSoloRoom` server-side ↔ `createGameStore` here), exactly like Hokm:
 * the same `{view, legal}` frames, the same two-option «خروج» (keep resumable)
 * vs «پایان بازی» (acked server-side end) exit flow. */
function HeartsSoloRound({
  difficulty,
  onAgain,
}: {
  /** FIXED for this game — chosen on the start screen, never changes mid-game. */
  difficulty: BotDifficulty;
  onAgain: () => void;
}): ReactNode {
  const setView = useSession((s) => s.setView);
  const initData = useSession((s) => s.initDataRaw);
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [round, setRound] = useState(0);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endFailed, setEndFailed] = useState(false);

  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<HeartsMove, HeartsView>({
          wsUrl: GAME_SERVER_URL,
          room: "hearts",
          getInitData: () => initData,
          // FIXED for this game — chosen on the start screen, never changes mid-game.
          difficulty,
        }),
      ),
    // `round` bumps to start a fresh game (new connection) on "play again".
    [initData, round, difficulty],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  // Hold the summary overlay for a beat so the final move/trick animates first.
  const showEnd = useEndReveal(result !== null);
  // The win/lose chime lands WITH the summary (it used to fire ~1.4s early).
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  // The app-root reconnect overlay owns the screen while it's up — don't ALSO
  // paint this container's raw-error screen behind it (that leaked English).
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      setView("lobby");
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  // The classic SVG faces are a separate chunk (see `game/card-faces.ts`): the table may not
  // be revealed until they are in, or it would deal blank cards. The bar's floor is a MINIMUM,
  // not a timeout — on a slow connection it simply waits longer.
  const deck = useContext(CardDeckContext);
  const faces = useDeckGate(deck);
  const { loading, progress, waitingForAssets } = useSoloLoad(
    view != null,
    ready,
    store,
    faces.ready,
  );
  // the deck this table DRAWS: latched per hand from the felt's first card — no hand while the
  // loading screen is up (`hand-deck.ts`, N1) — a pick lands at the next deal, never mid-hand
  const drawDeck = useHandDeck(deck, loading ? null : handKeyOf("hearts", view));

  // «خروج» — leave but KEEP the game resumable (a plain disconnect no longer ends it).
  const doLeave = (): void => {
    store.getState().disconnect();
    setView("lobby");
  };
  // «پایان بازی» — END the game server-side and WAIT for the server's ack (the
  // ActiveGame row is provably deleted) BEFORE dropping it locally and leaving. On
  // failure/timeout stay in the game, keep it listed as in-progress (it IS), and
  // surface a retry dialog — never navigate-and-forget an unconfirmed end.
  const doEnd = (): void => {
    if (ending) return;
    setEnding(true);
    setEndFailed(false);
    void store
      .getState()
      .endGame()
      .then(() => {
        clearActiveGame("hearts"); // off the resume strip right away; Lobby refetch confirms
        doLeave();
      })
      .catch(() => setEndFailed(true))
      .finally(() => setEnding(false));
  };
  // The in-game exit is gated behind the two-option confirm dialog first.
  const leave = (): void => setConfirmingLeave(true);
  // «بازی دوباره» returns to the START SCREEN rather than re-dealing at the same settings —
  // the same flow حکم/شطرنج/منچ/تخته‌نرد use, so the next game can be set up before it deals.
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.hearts.connectionError}
          </p>
          <Button variant="primary" onClick={() => setView("lobby")}>
            {fa.hearts.leave}
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !view) {
    // Retries are spent and the artwork still is not here: an explicit, recoverable screen —
    // never a silent stall, and never a table dealt with blank cards.
    if (faces.failed) {
      return <Boot error={fa.status.offline} onRetry={faces.retry} onLeave={doLeave} />;
    }
    return (
      <Boot
        caption={waitingForAssets ? fa.status.preparingCards : fa.hearts.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.hearts.leave}
      />
    );
  }

  return (
    <CardDeckContext.Provider value={drawDeck}>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. */}
      <HeartsTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : leave}
        self={profile}
      />
      {result && showEnd ? (
        // Already over (ActiveGame cleared by the terminal finish) → plain exit.
        <WinOverlay
          result={result}
          totalScores={view.totalScores}
          you={view.you}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            doEnd();
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
      {endFailed ? (
        // The server never ACKed the end — the game is still in progress and still
        // on the resume list. Same shared dialog, as the error surface: retry or stay.
        <ConfirmDialog
          title={fa.confirm.endFailedTitle}
          body={fa.confirm.endFailedBody}
          confirmLabel={fa.confirm.endRetry}
          onConfirm={doEnd}
          onCancel={() => setEndFailed(false)}
        />
      ) : null}
    </CardDeckContext.Provider>
  );
}

/**
 * Owns the START SCREEN → game flow. The opponent level and the card design are chosen here,
 * before the deal, and stay FIXED for the game; «بازی دوباره» comes back to ask again.
 *
 * A RESUME stands the screen aside ({@link takeResumeIntent}): the saved game already carries
 * the settings it began with, and the lobby arms that signal for every solo resume.
 */
export function HeartsGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getBotDifficulty());
  const [deck, setDeck] = useState<CardDeckId>(getCardDeck());

  if (phase === "select") {
    return (
      <SoloStartScreen
        title={fa.hearts.title}
        emoji="♥️"
        heading={fa.soloStart.heading}
        leaveLabel={fa.hearts.leave}
        difficulty={difficulty}
        onDifficultyChange={(v) => {
          setDifficulty(v);
          setBotDifficulty(v);
        }}
        deck={deck}
        onDeckChange={(v) => {
          setDeck(v);
          setCardDeck(v);
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      />
    );
  }
  // The DECK CHOSEN ON THE START SCREEN, not the one App read when it rendered: App computes
  // `getCardDeck()` once, above this component, and never re-renders on a phase flip — so the
  // pick would not reach the table without this nested provider (the inner one wins).
  return (
    <CardDeckContext.Provider value={deck}>
      <HeartsSoloRound difficulty={difficulty} onAgain={() => setPhase("select")} />
    </CardDeckContext.Provider>
  );
}
